// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// CareRx clinical lane + PA feed — TanStack Query hooks (E-02: components never call api.* directly).

import { useCallback } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  answerDrugAssessment, assignReviewTo, dismissCandidate, getClinicalCounts, getDrugAssessment, getReviewAssignment,
  listDismissedCandidates, listReviewAssignees, placeManualHold, restoreCandidate, setHoldFollowUp, setLabNextAction,
  type AssignContext,
} from '../../api/carerxClinical'
import { listCareRxTherapies } from '../../api/client'

export const clinicalKeys = {
  counts: ['carerx', 'clinical-counts'] as const,
  dismissed: ['carerx', 'feed-dismissed'] as const,
  dismissedPage: (page: number, pageSize: number) => ['carerx', 'feed-dismissed', page, pageSize] as const,
  drugAssessment: (therapyId: string) => ['carerx', 'drug-assessment', therapyId] as const,
  openTherapies: (patientId: string) => ['carerx', 'open-therapies', patientId] as const,
  assignees: ['carerx', 'review-assignees'] as const,
  assignment: (therapyId: string) => ['carerx', 'review-assignment', therapyId] as const,
}

/** Review · Renewals · Hold counts. Each stage's list refreshes them when it reloads. */
export function useClinicalCounts(enabled = true) {
  return useQuery({ queryKey: clinicalKeys.counts, queryFn: getClinicalCounts, staleTime: 30_000, enabled })
}

/** For a stage list's reload: its action may have moved a therapy between stages. Stable identity. */
export function useRefreshClinicalCounts() {
  const qc = useQueryClient()
  return useCallback(() => { void qc.invalidateQueries({ queryKey: clinicalKeys.counts }) }, [qc])
}

export function useSetHoldFollowUp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { therapyId: string; followUpOn: string | null }) => setHoldFollowUp(v.therapyId, v.followUpOn),
    onSuccess: () => qc.invalidateQueries({ queryKey: clinicalKeys.counts }),
  })
}

export function useDismissedCandidates(page: number, pageSize: number, enabled: boolean) {
  return useQuery({
    queryKey: clinicalKeys.dismissedPage(page, pageSize),
    queryFn: () => listDismissedCandidates(page, pageSize),
    placeholderData: keepPreviousData,
    enabled,
  })
}

/** Dismiss or restore; the dismissed list goes stale, and `onDone` reloads the PA feed. */
export function useDismissCandidate(onDone: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { rxId: string; reason: string }) => dismissCandidate(v.rxId, v.reason),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: clinicalKeys.dismissed }); onDone() },
  })
}
export function useRestoreCandidate(onDone: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (rxId: string) => restoreCandidate(rxId),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: clinicalKeys.dismissed }); onDone() },
  })
}

/** ASP #13: the therapy's drug-specific questions with their current answers. */
export function useDrugAssessment(therapyId: string) {
  return useQuery({ queryKey: clinicalKeys.drugAssessment(therapyId), queryFn: () => getDrugAssessment(therapyId) })
}
export function useAnswerDrugAssessment(therapyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { questionId: string; answer: string }) => answerDrugAssessment(therapyId, v.questionId, v.answer),
    onSuccess: () => qc.invalidateQueries({ queryKey: clinicalKeys.drugAssessment(therapyId) }),
  })
}

/** ASP #28: save the typed next action; the caller reloads its list. */
export function useSetLabNextAction() {
  return useMutation({ mutationFn: (v: { therapyId: string; text: string | null }) => setLabNextAction(v.therapyId, v.text) })
}

/** ASP #24: the patient's therapies that can be put on hold (not ended). */
export function useOpenTherapies(patientId: string | null) {
  return useQuery({
    queryKey: clinicalKeys.openTherapies(patientId ?? ''),
    queryFn: async () => (await listCareRxTherapies(patientId!)).therapies.filter(t => !t.is_terminal),
    enabled: !!patientId,
  })
}
export function usePlaceManualHold() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { therapyId: string; reason: string }) => placeManualHold(v.therapyId, v.reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: clinicalKeys.counts }),
  })
}

/** ASP #32: who a review can be assigned to (fetched when the picker opens). */
/** The pharmacist roster with its fallback flag (one fetch shared by every picker). */
export function usePharmacistRoster(enabled: boolean) {
  return useQuery({
    queryKey: clinicalKeys.assignees,
    queryFn: async () => { const r = await listReviewAssignees(); return { items: r.items, fallback: !!r.fallback } },
    enabled,
    staleTime: 5 * 60_000,
  })
}
export function useReviewAssignees(enabled: boolean) {
  const q = usePharmacistRoster(enabled)
  return { ...q, data: q.data?.items }
}
export function useAssignReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { therapyId: string; rphId: string; keepStatus?: boolean; context?: AssignContext }) =>
      assignReviewTo(v.therapyId, v.rphId, v.keepStatus, v.context),
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: clinicalKeys.assignment(v.therapyId) }),
  })
}

/** ASP #27/#28: the therapy's pharmacist, for the care-plan panels. */
export function useReviewAssignment(therapyId: string) {
  return useQuery({ queryKey: clinicalKeys.assignment(therapyId), queryFn: () => getReviewAssignment(therapyId) })
}
/** After a care plan is completed — the server may have named the completing pharmacist. */
export function useRefreshReviewAssignment() {
  const qc = useQueryClient()
  return useCallback((therapyId: string) => { void qc.invalidateQueries({ queryKey: clinicalKeys.assignment(therapyId) }) }, [qc])
}
